import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/ui/pill';
import {
  useMarkNotificationRead,
  useNotificationDestinationAvailability,
  useUserNotification,
} from '@/lib/hooks/useUserNotifications';
import {
  formatNotificationDateTime,
  getNotificationCtaLabel,
  getNotificationRouteTarget,
  getNotificationPillVariant,
  getNotificationTypeLabel,
  getNotificationUnavailableMessage,
} from '@/lib/utils/notifications.utils';

interface NotificationDetailsProps {
  notificationId: string;
}

export function NotificationDetails({
  notificationId,
}: NotificationDetailsProps) {
  const navigate = useNavigate();
  const {
    data: notification,
    isLoading,
    error,
  } = useUserNotification(notificationId);
  const markRead = useMarkNotificationRead();
  const {
    data: destinationAvailable = false,
    isLoading: isCheckingDestination,
  } = useNotificationDestinationAvailability(notification);

  useEffect(() => {
    if (!notification || notification.read_at) {
      return;
    }

    void markRead.mutateAsync(notification.id).catch((mutationError) => {
      console.error('Failed to mark notification read:', mutationError);
    });
  }, [markRead, notification]);

  if (isLoading) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 pb-8">
        <div className="space-y-4 animate-pulse">
          <div className="h-6 w-28 rounded bg-slate-700" />
          <div className="h-10 w-3/4 rounded bg-slate-700" />
          <div className="h-40 rounded-2xl bg-slate-700" />
        </div>
      </div>
    );
  }

  if (error || !notification) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 pb-8">
        <p className="text-body text-red-400">
          {error
            ? 'Failed to load notification. Please try again later.'
            : 'Notification not found.'}
        </p>
      </div>
    );
  }

  const handleOpenDestination = () => {
    const routeTarget = getNotificationRouteTarget(
      notification,
      'notifications'
    );

    if (routeTarget) {
      void navigate(routeTarget);
      return;
    }

    window.location.assign(notification.destination_url);
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-4 pb-8">
      <div className="rounded-2xl bg-slate-700 p-5">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Pill
            variant={getNotificationPillVariant(notification.notification_type)}
          >
            {getNotificationTypeLabel(notification.notification_type)}
          </Pill>
          <span className="text-small text-slate-400">
            {formatNotificationDateTime(notification.created_at)}
          </span>
        </div>

        <h1 className="font-field-74 text-h1 text-slate-50 mb-4">
          {notification.title}
        </h1>

        <div className="text-body text-slate-200 whitespace-pre-line [overflow-wrap:anywhere]">
          {notification.body}
        </div>
      </div>

      {!isCheckingDestination && !destinationAvailable && (
        <div className="mt-4">
          <Banner
            variant="warning"
            title="Destination unavailable"
            description={getNotificationUnavailableMessage(notification)}
          />
        </div>
      )}

      <div className="mt-6">
        <Button
          type="button"
          size="lg"
          className="w-full"
          onClick={handleOpenDestination}
          disabled={!destinationAvailable || isCheckingDestination}
        >
          {isCheckingDestination
            ? 'Checking destination…'
            : getNotificationCtaLabel(notification)}
        </Button>
      </div>
    </div>
  );
}
